import { DateTime } from "luxon";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { IDocumentUpdateFormProps } from "../../../../@types/documents";
import { Button } from "../../../../components/buttons/button";
import { SettingsButtonModal } from "../../../../components/buttons/iconButtons/settingsButton/SettingsButtonModal";
import { Accordion } from "../../../../components/controls/accordion";
import GridSelectorInput from "../../../../components/controls/GridSelectorInput";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextInput } from "../../../../components/controls/inputs";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import { IOption, Select } from "../../../../components/selects/select";
import { IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import { IGoodsDTO } from "../../../../libs/coreapi-dto/dirs/goods";
import { IInvoiceItem, IPricingInvoiceViewDTO } from "../../../../libs/coreapi-dto/dirs/pricingInvoice";
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider";
import { GoodsDataProvider } from "../../../../Services/DataProviders/GoodsDataProvider";
import { PricingInvoiceDataProvider } from "../../../../Services/DataProviders/PricingInvoiceDataProvider";
import { PricingModelDataProvider } from "../../../../Services/DataProviders/PricingModelDataProvider";
import { StoreDataProvider } from "../../../../Services/DataProviders/StoreDataProvider";
import { useForm } from "../../../../system/hooks/useForm";
import useGridFilter from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../../system/providers/plugin";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { GoodsSelectorModal } from "../../../Dictionaries/Goods";
import KizScanModal from "../../../Dictionaries/Kiz/ScanModal/KizScanModal";
import { PricingModelSelectorModal } from "../../../Dictionaries/PricingModel";
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair";
import styles from "../styles/ImportRemainsCreatorView.module.scss";
import { DocumentType, IdTableVariant, SearchOption } from "../../../../@types/enumsGlobal";
import { IGoodsByIdDTO } from "../../../../libs/core-api-requests/dirs/goodsRequest";
import PositionEditPanel from "../../../../components/commandsPanels/PositionEditPanel";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import { StockBalanceButtonCompress, StockBalanceButtonExpand } from "../../../../components/buttons/iconButtons/special/QuickAccessBarCollection";
import Tooltip from "../../../../components/tooltips/Tooltip";
import { IScalingRatioDTO } from "../../../../libs/coreapi-dto/dirs/scalingRatio";
import { ScalingRatioDataProvider } from "../../../../Services/DataProviders/ScalingRatioDataProvider";
import { autocomplete } from "../../../../system/functions/autocomplete";
import { getStyleCell } from "../../../../system/functions/getStyleCell";
import { controlKiz, countKizSum } from "../../../../system/functions/controlKiz";
import { IImportRemainsItemCreateDTO, IImportRemainsItemEditDTO, IImportRemainsItemGetDTO } from "../../../../libs/coreapi-dto/documents/importRemainsItem";
import { IImportRemainsCreateDTO, IImportRemainsGetDTO, IImportRemainsUpdateDTO } from "../../../../libs/coreapi-dto/documents/importRemains";
import { collectPricingInvoiceDTO, collectPricingInvoiceItem, eventPricingInvoiceTypes } from "../../Invoice/CollectPricingInvoiceDTO";
import InvoiceGoodsSelectModal from "../../Invoice/InvoiceCreator/InvoiceGoodsSelectModal";
import { ImportRemainsCreatorItemGridPluginSettings } from "./ImportRemainsItemCreator/ImportRemainsItemCreatorGridPluginSettings";
import { ChangeableGrid, IChangeDataGrid } from "../../../../components/grids/changeableGrid/ChangeableGrid";
import { validateDuplicateKizs } from "../../../../system/functions/validateDuplicateKizs";
import { StoreSelectorPanel } from "../../../../components/storeSelectorPanel";
import { useTimeout } from "../../../../system/hooks/useTimeout";
import FlexRow from "../../../../components/controls/FlexRow";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { MoneyFormatComponent } from "../../../../components/controls/inputs/BaseInput";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { addValueToArray } from "../../../../system/functions/addValToArray";
import renderGlobalAlert from "../../../../system/hooks/useGlobalAlert";

interface IImportRemainsItemDTOForRecalculate extends IImportRemainsItemCreateDTO {
    forRecalculateGlobal: string;
}

interface ISum {
    amountRetailSum: number;
    amountRetailVatSum: number;
    amountRetailSumIncVat: number;
    amountSupplierVatSum: number;
    amountSupplierSum: number;
    amountSupplierSumIncVat: number;
}

interface IBaseFields {
    store: IEntitySimpleDTO;
    pricingModel: IEntitySimpleDTO;
    pharmacy: IEntitySimpleDTO;
}

interface ISelectedItemGoods extends IGoodsDTO {
    idGlobal: string;
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

interface IStoreModel {
    store?: IEntitySimpleDTO;
}

interface IValidator {
    supplier: IEntitySimpleDTO;
    pricingModel: IEntitySimpleDTO;
    documentDate: DateTime;
}

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

export type BtSaveOrOk = "ok" | "save";

interface ISum {
    amountRetailSum: number;
    amountRetailVatSum: number;
    amountRetailSumIncVat: number;
    amountSupplierVatSum: number;
    amountSupplierSum: number;
    amountSupplierSumIncVat: number;
}

interface IBaseFields {
    store: IEntitySimpleDTO;
    pricingModel: IEntitySimpleDTO;
    pharmacy: IEntitySimpleDTO;
}
export interface IDisplayItem {
    idGlobal: string;
    goodsName: string;
    scalingRatioName: string;
    quantity: number;
    goodsCode: string,
    producer: string,
    producerPrice: number;
    productMargin: number;
    retailPrice: number;
    retailPriceIncVat: number;
    retailVat: number;
    retailSum:number
    retailSumIncVat:number
    supplierPrice: number;
    supplierVat: number;
    supplierSum: number;
    supplierSumIncVat: number;
    isKiz: boolean;
    isGnvls: boolean;
    countKizScan: number | undefined;
    supplier: string | undefined;
    numerator: number;
    denominator: number;
    dateCreated: DateTime;
    isOsu?: boolean
    barCode: string
    goodsBarcodes?: string
    storePlaceName?: string
}

interface ISelectedItemGoods extends IGoodsDTO {
    idGlobal: string;
}

interface IGoodsViewItem {
    item: IGoodsByIdDTO[] | null;
    show: boolean;
}

const ImportRemainsUpdateForm: FC<IDocumentUpdateFormProps<IImportRemainsGetDTO, IImportRemainsItemGetDTO[], IImportRemainsUpdateDTO>> = (props) => {
    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const pluginCtx = usePluginContext();

    const sdp = new StoreDataProvider(appContext.coreApiService);
    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const cdp = new ContractorDataProvider(appContext.coreApiService);
    const pdp = new PricingModelDataProvider(appContext.coreApiService);
    const pricingInvoiceDataProvider = new PricingInvoiceDataProvider(appContext.coreApiService);
    const scalingRatioDP = new ScalingRatioDataProvider(appContext.coreApiService);
    const userContext = useUserContext();
    const idTable = IdTableVariant.ImportRemains
    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const [onlyRead, setOnlyRead] = useState((props.variant === 'edit' && (props.document?.documentState === 'proc' || lockFromPermission)) ? true : false);
    const [gridId] = useState(uuidv4());

    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string|undefined>();

    const [data, setData] = useState<IImportRemainsUpdateDTO>(
        props.documentUpdate ??({ idGlobal: uuidv4(), documentDate: props.isTest ? DateTime.now().plus({ days: -7 }) : DateTime.now() } as IImportRemainsCreateDTO)
    );

    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItemGoods, setSelectedItemGoods] = useState<ISelectedItemGoods>();
    const [selectedItemCreateDto, setSelectedItemCreateDto] = useState<IImportRemainsItemCreateDTO>();
    const [gridFilter, dispatchGridFilter] = useGridFilter();

    const [averageMarkup, setAverageMarkup] = useState<string>(recalculateAverageMarkup(data.items)); // средняя наценка

    const [mnemocode, setMnemocode] = useState<string>(props.document?.mnemocode ?? "");
    const [supplier, setSupplier] = useState<IEntitySimpleDTO[]>(props.documentItems?.map((item) => item.supplier) ?? ([{ displayName: "", idGlobal: "" } as IEntitySimpleDTO] as IEntitySimpleDTO[]));

    const [sum, setSum] = useState<ISum>({ amountRetailSum: 0, amountRetailVatSum: 0, amountRetailSumIncVat: 0, amountSupplierSum: 0, amountSupplierSumIncVat: 0, amountSupplierVatSum: 0 });
    const [showKizScan, setShowKizScan] = useState<boolean>(false);

    const { t } = useTranslation();
    const errorsT = (value: string) => t("errors." + value);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: "" });

    const defaultSearchOption = { displayName: t("documents.invoice.name"), value: "name" } as IOption;
    const [searchOption, setSearchOption] = useState<IOption>(defaultSearchOption);

    const [selectedScalingRation, setSelectedScalingRation] = useState<IScalingRatioDTO | null>(null);
    const [goodsViewItem, setGoodsViewItem] = useState<IGoodsViewItem>({ item: null, show: false });

    const [isCompact, setIsCompact] = useState<boolean>(false);
    const [isCompactStore, setIsCompactStore] = useState<boolean>(false);
    //Validations
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]);

    const [pricingModel, setPricingModel] = useState<IEntitySimpleDTO | null>(props.document?.pricingModel ?? null);
    const [store, setStore] = useState<IEntitySimpleDTO | null>(props.document?.store ?? null);
    const [pharmacy, setPharmacy] = useState<IEntitySimpleDTO | null>(null);

    const currentIdTab = useRef<string>('');

    useEffect(() => setData({ ...data, idStoreGlobal: store?.idGlobal }), [store]);
    useEffect(() => setData({ ...data, idPricingModelGlobal: pricingModel?.idGlobal }), [pricingModel]);

    useEffect(() => setStore(props.document?.store ? { ...props.document?.store } : null), [props.document?.store]);
    useEffect(() => setPricingModel(props.document?.pricingModel ? { ...props.document?.pricingModel } : null), [props.document?.pricingModel]);

    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });
    const [setTimer, clearTimer]  = useTimeout()

    const { isValid, errors, setErrors } = useForm<IStoreModel & IValidator>({
        validations: {
            store: {
                required: {
                    value: data?.idStoreGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            pricingModel: {
                required: {
                    value: data?.idPricingModelGlobal ? false : true,
                    message: errorsT("required"),
                },
            },
            documentDate: {
                required: {
                    value: data?.documentDate?.day ? false : true,
                    message: errorsT("required")
                }
            },
        },
    });

    useEffect(() => {
        if (selectedItem) {
            let selectedItemDto = data.items.find((x) => x.idGlobal === selectedItem.idGlobal) as IImportRemainsItemCreateDTO;
            setSelectedItemCreateDto(selectedItemDto);
            scalingRatioDP.overrideGetById(selectedItemDto.idGoodsGlobal, selectedItemDto.idScalingRatioGlobal as string, (e) => {
                setSelectedScalingRation(e);
            });
            goodsDP.getById(data.items.find((x) => x.idGlobal === selectedItem.idGlobal)?.idGoodsGlobal as string, (e) => {
                setSelectedItemGoods({ ...e, idGlobal: data.items.find((x) => x.idGlobal === selectedItem.idGlobal)?.idGoodsGlobal as string });
            });
        } else {
            setSelectedItemCreateDto(undefined);
            setSelectedItemGoods(undefined);
            setSelectedScalingRation(null);
        }
    }, [selectedItem]);

    useEffect(() => {
        calculateSum()
    }, [data?.items,displayItems]);

    const calculateSum = () => {
        let amountRetailSumTemp = 0; //Сумма роз. б/НДС
        let amountRetailVatSumTemp = 0; //Сумма НДС розн.
        let amountRetailSumIncVatTemp = 0; //Сумма розничная с НДС //Сумма розн.

        let amountSupplierVatSumTemp = 0; //Суммы НДС пост.
        let amountSupplierSumTemp = 0; //Сумма пост. б/НДС
        let amountSupplierSumIncVatTemp = 0; //Сумма пост.
        if (data?.items) {
            data?.items.map((ii) => {
                amountRetailSumTemp += ii.retailCostInfo.sum;
                amountRetailVatSumTemp += ii.retailCostInfo.vatSum;
                amountRetailSumIncVatTemp += ii.retailCostInfo.sumIncVat;
                amountSupplierVatSumTemp += ii.supplierCostInfo.vatSum;
                amountSupplierSumTemp += ii.supplierCostInfo.sum;
                amountSupplierSumIncVatTemp += ii.supplierCostInfo.sumIncVat;
            });
            setSum({
                ...sum,
                amountRetailSum: amountRetailSumTemp,
                amountRetailVatSum: amountRetailVatSumTemp,
                amountRetailSumIncVat: amountRetailSumIncVatTemp,
                amountSupplierVatSum: amountSupplierVatSumTemp,
                amountSupplierSum: amountSupplierSumTemp,
                amountSupplierSumIncVat: amountSupplierSumIncVatTemp,
            });
        }
    }

    function generateDisplayItems(item : IImportRemainsItemGetDTO) {
        return {
            idGlobal: item.idGlobal, 
            goodsName: item.goods?.displayName, 
            scalingRatioName: item.scalingRatio?.displayName, 
            quantity: item.quantity, 

            productMargin: item.productMargin, 
            producerPrice: item.producerPrice, 
            goodsCode: item.goodsCode,
            producer: item.producer,
            
            retailPrice: item.retailCostInfo?.price, 
            retailPriceIncVat: item.retailCostInfo?.priceIncVat,
            retailSum: item.retailCostInfo?.sum,
            retailSumIncVat: item.retailCostInfo?.sumIncVat,
            retailVat: item.retailCostInfo?.vat,

            supplierSum: item.supplierCostInfo?.sum,
            supplierSumIncVat: item.supplierCostInfo?.sumIncVat,
            supplierPrice: item.supplierCostInfo?.price,
            supplierVat: item.supplierCostInfo?.vat,
       
            supplier: item.supplier?.displayName, 

            isKiz: item.isKiz || item.isKizBox, 
            countKizScan: (item.isKiz || item.isKizBox) === true ? item.kizBoxCount + item.kizCount : undefined, 
            isGnvls: item.isGnvls, 
            denominator: item.denominator,
            numerator: item.numerator,
            isOsu: item.isOsu,
            dateCreated: item.dateCreated,
            barCode: item.barCode,
            goodsBarcodes: item?.goodsBarcodes,
            storePlaceName: item?.storePlace?.displayName
        } as IDisplayItem;
    }

    useEffect(() => {
        if (tabsContext?.currentTab) {
            currentIdTab.current = tabsContext.currentTab?.id;
        }
        if (props.document && props.documentItems) {
            sdp.getById(props.document.store.idGlobal, (e) => {
                if (props.document) {
                    setPharmacy(e.contractor);
                }
            });
            setDisplayItems(props.documentItems.map(el => generateDisplayItems(el)));
        }
        if ((props.variant === "edit" || props.variant === "copy") && props.document) {
            let arr: IItemFilter[] = [];
            props.document.items?.forEach((x) => {
                arr.push({
                    idItem: x.idGlobal as string,
                    quantityMax: 9999999,
                    quantityMin: 0,
                });
            });
            setItemsFilter(arr);
            const propsValidateItems = props.document.items.map((el) => ({
                err: false,
                message: "",
                idGlobal: el.idGlobal,
            })) as IValidationItem[];
            setItemsValidation(propsValidateItems);
        }
    }, []);

    useEffect(() => {
        if (props.variant === "create") {
            if (props.isTest) {
                autocomplete(props.isTest).then((e) => {
                    if (e.contractorMe) {
                        sdp.getById(e.contractorMe?.store?.idGlobal, (store) => {
                            props.ok({
                                ...data,
                                idPricingModelGlobal: store.pricingPricingModel?.idGlobal ?? e.contractorMe?.pricingPricingModel?.idGlobal,
                                idStoreGlobal: e.contractorMe?.store?.idGlobal,
                                idSupplierGlobal: e.defaultSupplier?.idGlobal,
                                items: [e.invoiceItem as IImportRemainsItemCreateDTO],
                            });
                        });
                    }
                });
            } else {

            }
        }
    }, []);

    useEffect(() => {
        const handleTabClose = (event) => {
            lockingDocuments.delete({
                idTable: IdTableVariant.ImportRemains,
                idDocument: data.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    // Блокировка документа
    const requestUpdateLockingDocumentTimeout = useRef<NodeJS.Timeout>();
    const isUpdatingLockingDocument = useRef(false);

    useEffect(() => {
        // Если документ не определён — ничего не делаем
        if (!idLockingDocument) return;

        // Если уже идёт обновление — не ставим новый таймер
        if (isUpdatingLockingDocument.current) return;

        // Отменяем предыдущий таймер
        if (requestUpdateLockingDocumentTimeout.current) {
            clearTimeout(requestUpdateLockingDocumentTimeout.current);
        }

        clearTimeout(requestUpdateLockingDocumentTimeout.current as NodeJS.Timeout);
        requestUpdateLockingDocumentTimeout.current = setTimeout(() => {

            isUpdatingLockingDocument.current = true;

            lockingDocuments.update(idLockingDocument, (e) => {
                isUpdatingLockingDocument.current = false;
            },
                () => {
                    isUpdatingLockingDocument.current = false;
                })
        }, Number(process.env.REACT_APP_INTERVAL_REQUEST));
    }, [data.documentDate, data.idPricingModelGlobal, data.idStoreGlobal, data.datePay, data.items]);

    useEffect(() => {
        if (props.variant === "edit"&& props.documentUpdate&& props.documentItems && props.document) {

            lockingDocuments.check(data.idGlobal as string, (e)=>{
                if (e) setOnlyRead(true)
                else
                    lockingDocuments.send({
                        idTable: IdTableVariant.ImportRemains,
                        idDocument: data.idGlobal as string,
                    }, (e)=> {
                        setIdLockingDocument(e)
                    });
            })

            setData(props.documentUpdate);
            setMnemocode(props.document.mnemocode);
            setDisplayItems(props.documentItems.map((x) => generateDisplayItems(x)));
        }
    }, [props.variant]);

    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
          }
        if (!data.idPricingModelGlobal) return;
        handleRecalculate();
    },[data.idPricingModelGlobal]);

    /*const recalculatePricingItems = (eventTypes: eventPricingInvoiceTypes) => {
        if (data?.idGlobal !== undefined && data?.idStoreGlobal !== undefined && data.items) {
            let invoiceItemsWithTempGlobal: IImportRemainsItemDTOForRecalculate[] = [];
            let invoiceItemsForRequest: IInvoiceItem[] = [];
            data.items.map((ii) => {
                let uuid = ii.idGlobal ?? uuidv4();
                invoiceItemsWithTempGlobal.push({ ...ii, forRecalculateGlobal: uuid });

                let invoiceItem = collectPricingInvoiceItem(
                    uuid, 
                    ii.idGoodsGlobal, 
                    ii.idScalingRatioGlobal, 
                    ii.quantity, 
                    ii.producerPrice, 
                    {...ii.retailCostInfo, adprice: 0}, 
                    ii.supplierCostInfo, 
                    ii.registerPrice
                );
                invoiceItemsForRequest.push(invoiceItem);
            });
            let pricingInvoiceDTO = collectPricingInvoiceDTO(eventTypes, data.idGlobal, data.items[0]?.idSupplierGlobal, data.idStoreGlobal, invoiceItemsForRequest);

            pricingInvoiceDataProvider.getCalculatedInvoiceViewAsync(data?.idPricingModelGlobal as string, pricingInvoiceDTO, (e) => {
                
                e.items.forEach((x, index) => {
                    let indexItem = data.items.findIndex((item) => item.idGlobal === x.idGlobal);
                    let takeDataObj = data.items.find((item) => item.idGlobal === x.idGlobal) as IImportRemainsItemCreateDTO;
        
                    let indexDisplayItem = displayItems.findIndex((item) => item.idGlobal === x.idGlobal);
                    let takeDisplayObj = displayItems.find((item) => item.idGlobal === x.idGlobal) as IDisplayItem;

                    if (x.retailPriceChangeReason) {
                        setDisplayItems([ ...addValueToArray(displayItems, indexDisplayItem, { ...takeDisplayObj, retailPriceIncVat: 0, }),]);
                        renderGlobalAlert({ variant: "warning", statusCode: 500, title: x.retailPriceChangeReason, instance: "Сервис расчета цен" });
                    }

                    let newItem: IImportRemainsItemCreateDTO = {
                        ...takeDataObj,
                        retailCostInfo: {
                            vat: x.percentageOfVatRateRetail,
                            price: x.retailPrice,
                            vatPrice: x.retailVatPerUnit,
                            priceIncVat: x.retailPriceWithVat,
                            sum: x.retailAmount,
                            vatSum: x.vatAmountRetail,
                            sumIncVat: x.retailAmountWithVat,
                            adprice: x.percentageOfMarkupRetail,
                        },
                        supplierCostInfo: {
                            vat: x.supplierVatRatePercentage,
                            price: x.supplierPriceExcludingVat,
                            vatPrice: x.supplierVatPerUnit,
                            priceIncVat: x.supplierPriceWithVat,
                            sum: x.supplierAmountExcludingVat,
                            vatSum: x.supplierVatAmount,
                            sumIncVat: x.supplierAmountWithVat,
                            adprice: x.supplierMarkupPercentage,
                        },
                        quantity: x.quantity,
                        registerPrice: x.registeredPrice,
                        producerPrice: x.manufacturerPrice,
                        productMargin: x.actualMarkupPercentage,
                    };
                    
                    setData({
                        ...data,
                        items: addValueToArray(data.items, indexItem, newItem),
                    });

                    displayItems.length > 0 && setDisplayItems(prev => addValueToArray(
                        prev,
                        indexDisplayItem,
                        {
                            ...takeDisplayObj,
                            producerPrice: x.manufacturerPrice,
                            quantity: x.quantity,
                            productMargin: x.percentageOfMarkupRetail,
                            retailVat: x.percentageOfVatRateRetail,
                            retailPrice: x.retailPrice,
                            retailPriceIncVat: x.retailPriceWithVat,
                            retailSum: x.retailAmount,
                            retailSumIncVat: x.retailAmountWithVat,
                            supplierPrice: x.supplierPriceExcludingVat,
                            supplierVat: x.supplierVatRatePercentage,
                            supplierSum: x.supplierAmountExcludingVat,
                            supplierSumIncVat: x.supplierAmountWithVat,
                        } as IDisplayItem
                    )
                    );
                })
            });
        }
    };*/

    const [isCalculating, setIsCalculating] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);

    const handleCancelRecalculation = () => {
        if (!abortController) return;

        setIsCancelling(true);
        abortController.abort();

        setTimeout(() => {
            setIsCancelling(false);
            setAbortController(null);
        }, 500);
    };

    const handleRecalculate = async () => {
        const controller = new AbortController();
        setAbortController(controller);
        
        await recalculatePricingItems('QuantityСhanged', controller);
    };

    /*const recalculatePricingItems = async (eventTypes: eventPricingInvoiceTypes, abortController: AbortController, chunkSize = 500) => {
        setIsCalculating(true);
        const maxConcurrentRequests = 4; // Оптимальное число параллельных запросов
        const activeRequests = new Set<Promise<void>>(); // Для отслеживания активных запросов
        let lastRequestTime = 0;

        try {
            if (!data?.idGlobal || !data.idStoreGlobal || !data.items.length) return;

            // Подготовка данных
            const invoiceItemsForRequest = data.items.map((ii) => ({
                uuid: ii.idGlobal ?? uuidv4(),
                item: ii,
                dto: collectPricingInvoiceItem(
                    ii.idGlobal ?? uuidv4(),
                    ii.idGoodsGlobal,
                    ii.idScalingRatioGlobal,
                    ii.quantity,
                    ii.producerPrice,
                    { ...ii.retailCostInfo, adprice: 0 },
                    ii.supplierCostInfo,
                    ii.registerPrice,
                    ii.numerator,
                    ii.denominator,
                    ii.isGnvls
                )
            }));

            // Создаем копии для НАКОПЛЕНИЯ изменений
            let currentDataItems = [...data.items];
            let currentDisplayItems = [...displayItems];
            const allAlerts: string[] = [];

            // Разбиваем на чанки
            const chunks: typeof invoiceItemsForRequest[] = [];
            for (let i = 0; i < invoiceItemsForRequest.length; i += chunkSize) {
                chunks.push(invoiceItemsForRequest.slice(i, i + chunkSize));
            }

            // Очередь обработки с ограниченным параллелизмом
            const processChunk = async (chunk: typeof invoiceItemsForRequest, chunkIndex: number) => {
                if (abortController.signal.aborted) return;

                // Формируем DTO для чанка
                const chunkDTO = {
                    ...collectPricingInvoiceDTO(
                        eventTypes,
                        data.idGlobal,
                        data.items[0]?.idSupplierGlobal,
                        data.idStoreGlobal,
                        chunk.map(c => c.dto)
                    ),
                    chunkIds: chunk.map(c => c.uuid)
                };

                const now = Date.now();
                const timeSinceLast = now - lastRequestTime;

                // Базовая задержка 30ms (было 100ms)
                const baseDelay = 30;

                // Умная задержка: только если запросы идут слишком часто
                if (timeSinceLast < baseDelay) {
                    const delay = baseDelay - timeSinceLast;
                    await new Promise(resolve => setTimeout(resolve, delay));
                }

                lastRequestTime = Date.now();

                try {
                    // Отправляем запрос
                    const response = await new Promise<IPricingInvoiceViewDTO>((resolve, reject) => {
                        const handleAbort = () => reject(new Error("Operation aborted"));
                        abortController.signal.addEventListener('abort', handleAbort, { once: true });

                        pricingInvoiceDataProvider.getCalculatedInvoiceViewAsync(
                            data.idPricingModelGlobal as string,
                            chunkDTO,
                            (result) => {
                                abortController.signal.removeEventListener('abort', handleAbort);
                                resolve(result);
                            },
                            //(error) => {
                            //    abortController.signal.removeEventListener('abort', handleAbort);
                            //    reject(error);
                            //},
                            abortController.signal
                        );
                    });

                    // Обновляем мапы для ТЕКУЩЕГО состояния
                    const dataItemsMap = new Map(
                        currentDataItems.map((item, i) => [item.idGlobal, i] as const)
                    );
                    const displayItemsMap = new Map(
                        currentDisplayItems.map((item, i) => [item.idGlobal, i] as const)
                    );

                    const chunkAlerts: string[] = [];
                    const updatedDataItems = [...currentDataItems];
                    const updatedDisplayItems = [...currentDisplayItems];

                    // Обрабатываем ответ
                    response.items.forEach(x => {
                        const dataIndex = dataItemsMap.get(x.idGlobal);
                        const displayIndex = displayItemsMap.get(x.idGlobal);
                        if (dataIndex === undefined || displayIndex === undefined) return;

                        // Обработка ошибок
                        if (x.retailPriceChangeReason) {
                            updatedDisplayItems[displayIndex] = {
                                ...updatedDisplayItems[displayIndex],
                                retailPriceIncVat: 0,
                            };
                            chunkAlerts.push(x.retailPriceChangeReason);
                            return;
                        }

                        // Обновление данных
                        updatedDataItems[dataIndex] = {
                            ...currentDataItems[dataIndex],
                            retailCostInfo: {
                                vat: x.percentageOfVatRateRetail,
                                price: x.retailPrice,
                                vatPrice: x.retailVatPerUnit,
                                priceIncVat: x.retailPriceWithVat,
                                sum: x.retailAmount,
                                vatSum: x.vatAmountRetail,
                                sumIncVat: x.retailAmountWithVat,
                                adprice: x.percentageOfMarkupRetail,
                            },
                            supplierCostInfo: {
                                vat: x.supplierVatRatePercentage,
                                vatPrice: x.supplierVatPerUnit,
                                price: x.supplierPriceExcludingVat,
                                priceIncVat: x.supplierPriceWithVat,
                                sum: x.supplierAmountExcludingVat,
                                vatSum: x.supplierVatAmount,
                                sumIncVat: x.supplierAmountWithVat,
                                adprice: x.supplierMarkupPercentage,
                            },
                            quantity: x.quantity,
                            producerPrice: x.manufacturerPrice,
                        };

                        updatedDisplayItems[displayIndex] = {
                            ...currentDisplayItems[displayIndex],
                            producerPrice: x.manufacturerPrice,
                            quantity: x.quantity,
                            retailPrice: x.retailPrice,
                            retailPriceIncVat: x.retailPriceWithVat,
                            retailVat: x.percentageOfVatRateRetail,
                            retailSum: x.retailAmount,
                            retailSumIncVat: x.retailAmountWithVat,
                            supplierPrice: x.supplierPriceExcludingVat,
                            supplierVat: x.supplierVatRatePercentage,
                            supplierSum: x.supplierAmountExcludingVat,
                            supplierSumIncVat: x.supplierAmountWithVat
                        };
                    });

                    // Обновляем состояние
                    currentDataItems = updatedDataItems;
                    currentDisplayItems = updatedDisplayItems;

                    setData(prev => ({
                        ...prev,
                        items: updatedDataItems
                    }));
                    setDisplayItems(prev => {
                        if (prev !== currentDisplayItems) {
                            console.warn("Обнаружено расхождение в состоянии displayItems");
                            return updatedDisplayItems;
                        }
                        return updatedDisplayItems;
                    });

                    // Сохраняем алерты
                    allAlerts.push(...chunkAlerts);
                } catch (error) {
                    if (isOperationAbortedError(error)) {
                        return;
                    }

                    renderGlobalAlert({
                        variant: "danger",
                        title: "Ошибка расчёта цен",
                        statusCode: 0,
                        detail: `Чанк ${chunkIndex + 1}/${chunks.length}: ${error}`,
                        instance: "Сервис расчета цен"
                    });
                }
            };

            // Очередь с ограниченным параллелизмом
            for (let i = 0; i < chunks.length; i++) {
                // Проверяем отмену перед каждым запросом
                if (abortController.signal.aborted) break;

                // Создаем промис для текущего чанка
                const chunkPromise = processChunk(chunks[i], i).finally(() => {
                    activeRequests.delete(chunkPromise);
                });

                activeRequests.add(chunkPromise);

                // Ограничиваем параллелизм
                if (activeRequests.size >= maxConcurrentRequests) {
                    await Promise.race(activeRequests);
                }

                // Даем браузеру обработать события
                if (i % 10 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 1));
                }
            }

            // Дожидаемся завершения всех запросов
            await Promise.allSettled(activeRequests);

            // Финальный алерт
            if (allAlerts.length > 0) {
                renderGlobalAlert({
                    variant: "warning",
                    title: `Ошибки в ${allAlerts.length} позициях`,
                    statusCode: 0,
                    detail: allAlerts.slice(0, 3).join("\n") + (allAlerts.length > 3 ? "\n..." : ""),
                    instance: "Сервис расчета цен",
                });
            }
        } catch (error) {
            if (!isOperationAbortedError(error)) {
                // Обработка критических ошибок
            }
        } finally {
            setIsCalculating(false);
        }
    };
    */

    // Утилита для проверки ошибки отмены
    const isOperationAbortedError = (error: unknown): boolean => {
        if (typeof error !== 'object' || error === null) return false;

        const err = error as any;
        return (
            err.name === 'AbortError' ||
            (err.message && err.message.includes('aborted')) ||
            (err instanceof DOMException && err.name === 'AbortError')
        );
    };

    const recalculatePricingItems = async (
        eventTypes: eventPricingInvoiceTypes,
        abortController: AbortController,
        chunkSize = 1000
    ) => {
        setIsCalculating(true);
        const allAlerts: string[] = [];
        const maxConcurrentRequests = 2;

        try {
            if (!data?.idGlobal || !data.idStoreGlobal || !data.items || !data.items.length) return;

            // 1. Подготовка данных
            const invoiceItemsForRequest = data.items.map((ii) => ({
                uuid: ii.idGlobal ?? uuidv4(),
                item: ii,
                dto: collectPricingInvoiceItem(
                    ii.idGlobal ?? uuidv4(),
                    ii.idGoodsGlobal,
                    ii.idScalingRatioGlobal,
                    ii.quantity,
                    ii.producerPrice,
                    { ...ii.retailCostInfo, adprice: 0 },
                    ii.supplierCostInfo,
                    ii.registerPrice,
                    ii.numerator,
                    ii.denominator,
                    ii.isGnvls
                )
            }));

            // 2. Разбиваем на чанки
            const chunks: typeof invoiceItemsForRequest[] = [];
            for (let i = 0; i < invoiceItemsForRequest.length; i += chunkSize) {
                chunks.push(invoiceItemsForRequest.slice(i, i + chunkSize));
            }

            // 3. Функция обработки одного чанка с функциональным обновлением состояния
            const processChunk = async (chunk: typeof invoiceItemsForRequest, chunkIndex: number) => {
                if (abortController.signal.aborted) {
                    throw new Error("Operation aborted");
                }

                // Формируем DTO для чанка
                const chunkDTO = {
                    ...collectPricingInvoiceDTO(
                        eventTypes,
                        data.idGlobal,
                        data.items[0]?.idSupplierGlobal,
                        data.idStoreGlobal,
                        chunk.map(c => c.dto)
                    ),
                    chunkIds: chunk.map(c => c.uuid)
                };

                // Добавляем умную задержку для распределения нагрузки
                await new Promise(resolve => setTimeout(resolve, 30 * (chunkIndex % 10)));

                // Отправляем запрос
                const response = await new Promise<IPricingInvoiceViewDTO>((resolve, reject) => {
                    const handleAbort = () => reject(new Error("Operation aborted"));
                    abortController.signal.addEventListener('abort', handleAbort, { once: true });

                    pricingInvoiceDataProvider.getCalculatedInvoiceViewAsync(
                        data.idPricingModelGlobal as string,
                        chunkDTO,
                        (result) => {
                            abortController.signal.removeEventListener('abort', handleAbort);
                            resolve(result);
                        },
                        abortController.signal
                    );
                });

                // Собираем алерты для этого чанка
                const chunkAlerts = response.items
                    .filter(x => x.retailPriceChangeReason)
                    .map(x => x.retailPriceChangeReason);

                allAlerts.push(...chunkAlerts);

                // 4. Обновляем состояние data с использованием функционального обновления
                setData(prevData => {
                    if (!prevData) return prevData;

                    // Создаем мапу для быстрого поиска по idGlobal
                    const itemIndexMap = new Map(
                        prevData.items.map((item, index) => [item.idGlobal, index])
                    );

                    // Копируем массив items
                    const newItems = [...prevData.items];

                    // Обновляем каждый элемент из ответа
                    response.items.forEach(x => {
                        const itemIndex = itemIndexMap.get(x.idGlobal);
                        if (itemIndex === undefined) return;

                        newItems[itemIndex] = {
                            ...newItems[itemIndex],
                            ...(x.retailPriceChangeReason ? {} : {
                                retailCostInfo: {
                                    vat: x.percentageOfVatRateRetail,
                                    price: x.retailPrice,
                                    vatPrice: x.retailVatPerUnit,
                                    priceIncVat: x.retailPriceWithVat,
                                    sum: x.retailAmount,
                                    vatSum: x.vatAmountRetail,
                                    sumIncVat: x.retailAmountWithVat,
                                    adprice: x.percentageOfMarkupRetail,
                                },
                                supplierCostInfo: {
                                    vat: x.supplierVatRatePercentage,
                                    vatPrice: x.supplierVatPerUnit,
                                    price: x.supplierPriceExcludingVat,
                                    priceIncVat: x.supplierPriceWithVat,
                                    sum: x.supplierAmountExcludingVat,
                                    vatSum: x.supplierVatAmount,
                                    sumIncVat: x.supplierAmountWithVat,
                                    adprice: x.supplierMarkupPercentage,
                                },
                                quantity: x.quantity,
                                producerPrice: x.manufacturerPrice,
                            })
                        };
                    });

                    return {
                        ...prevData,
                        items: newItems
                    };
                });

                // 5. Обновляем состояние displayItems
                setDisplayItems(prevDisplayItems => {
                    // Создаем мапу для быстрого поиска
                    const displayIndexMap = new Map(
                        prevDisplayItems.map((item, index) => [item.idGlobal, index])
                    );

                    // Копируем массив
                    const newDisplayItems = [...prevDisplayItems];

                    // Обновляем каждый элемент
                    response.items.forEach(x => {
                        const displayIndex = displayIndexMap.get(x.idGlobal);
                        if (displayIndex === undefined) return;

                        if (x.retailPriceChangeReason) {
                            // В случае ошибки обнуляем розничную цену
                            newDisplayItems[displayIndex] = {
                                ...newDisplayItems[displayIndex],
                                retailPriceIncVat: 0,
                            };
                        } else {
                            // Нормальное обновление
                            newDisplayItems[displayIndex] = {
                                ...newDisplayItems[displayIndex],
                                producerPrice: x.manufacturerPrice,
                                quantity: x.quantity,
                                retailPrice: x.retailPrice,
                                retailPriceIncVat: x.retailPriceWithVat,
                                retailVat: x.percentageOfVatRateRetail,
                                retailSum: x.retailAmount,
                                retailSumIncVat: x.retailAmountWithVat,
                                productMargin: x.percentageOfMarkupRetail,
                                supplierPrice: x.supplierPriceExcludingVat,
                                supplierVat: x.supplierVatRatePercentage,
                                supplierSum: x.supplierAmountExcludingVat,
                                supplierSumIncVat: x.supplierAmountWithVat
                            };
                        }
                    });

                    return newDisplayItems;
                });

                // Небольшая задержка для визуального отклика
                await new Promise(resolve => setTimeout(resolve, 10));
            };

            // 6. Функция для ограничения параллельных запросов
            const processChunksWithLimit = async () => {
                const activeRequests = new Set<Promise<void>>();
                const errors: Error[] = [];

                for (let i = 0; i < chunks.length; i++) {
                    if (abortController.signal.aborted) {
                        throw new Error("Operation aborted");
                    }

                    // Создаем промис для текущего чанка
                    const chunkPromise = processChunk(chunks[i], i)
                        .catch(error => {
                            errors.push(error);
                            // Логируем ошибку, но не прерываем выполнение других чанков
                            console.error(`Error processing chunk ${i + 1}:`, error);

                            // Показываем алерт для ошибки чанка
                            if (!isOperationAbortedError(error)) {
                                renderGlobalAlert({
                                    variant: "warning",
                                    title: `Ошибка в чанке ${i + 1}/${chunks.length}`,
                                    statusCode: 0,
                                    detail: `Продолжаем обработку остальных позиций: ${error}`,
                                    instance: "Сервис расчета цен"
                                });
                            }
                        });

                    activeRequests.add(chunkPromise);

                    // Очищаем завершенные промисы
                    chunkPromise.finally(() => {
                        activeRequests.delete(chunkPromise);
                    });

                    // Если достигли лимита, ждем завершения хотя бы одного запроса
                    if (activeRequests.size >= maxConcurrentRequests) {
                        await Promise.race(activeRequests);
                    }
                }

                // Ждем завершения всех оставшихся запросов
                await Promise.allSettled(activeRequests);

                // Если были ошибки, бросаем исключение
                if (errors.length > 0 && errors.length === chunks.length) {
                    throw new Error("Все запросы завершились с ошибкой");
                }
            };

            // 7. Запускаем обработку
            await processChunksWithLimit();

            // 8. Финальный алерт с результатами
            if (allAlerts.length > 0) {
                // Группируем похожие ошибки
                const alertCounts = allAlerts.reduce((acc, alert) => {
                    acc[alert] = (acc[alert] || 0) + 1;
                    return acc;
                }, {} as Record<string, number>);

                const topAlerts = Object.entries(alertCounts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 3)
                    .map(([text, count]) => `${text}${count > 1 ? ` (${count} шт.)` : ''}`);

                renderGlobalAlert({
                    variant: "warning",
                    title: `Обработано с ошибками: ${allAlerts.length} позиций`,
                    statusCode: 0,
                    detail: topAlerts.join("\n") + (allAlerts.length > 3 ? "\n..." : ""),
                    instance: "Сервис расчета цен",
                });
            } else {
                // Успешное завершение
                renderGlobalAlert({
                    variant: "success",
                    title: "Расчет завершен",
                    statusCode: 0,
                    detail: `Успешно обработано ${data.items.length} позиций`,
                    instance: "Сервис расчета цен"
                });
            }

        } catch (error) {
            if (!isOperationAbortedError(error)) {
                console.error("Critical error in recalculatePricingItems:", error);
                renderGlobalAlert({
                    variant: "danger",
                    title: "Критическая ошибка расчета",
                    statusCode: 0,
                    detail: `Ошибка при пересчете: ${error}`,
                    instance: "Сервис расчета цен"
                });
            }
        } finally {
            setIsCalculating(false);
        }
    };

    function recalculateAverageMarkup(items: IImportRemainsItemCreateDTO[] | undefined): string {
        let result: number = 0;
        if (items && items.length) {
            for (let i = 0; i < items.length; i++) {
                let supplierSumIncVat: number = items[i].supplierCostInfo.price;
                supplierSumIncVat += (supplierSumIncVat * items[i].supplierCostInfo.vat) / 100;
                let retailSumIncVat: number = items[i].retailCostInfo.price;
                retailSumIncVat += (retailSumIncVat * items[i].retailCostInfo.vat) / 100;
                result += (supplierSumIncVat / retailSumIncVat) * 100;
            }
            result = result / items.length;
        }
        return result > 0 ? result.toFixed(2).toString() : "0";
    }

    function callOpenImportRemainsItemCreatorPlugin(entity: IEntitySimpleDTO) {
        tabsContext.openChild(
            "import_remains_item_creator_plugin",
            "create",
            undefined,
            (value: IImportRemainsItemCreateDTO, displayItem: IDisplayItem) => {
                setData({ ...data, items: data.items ? [...data.items?.filter((x) => x.idGlobal !== value.idGlobal), value] : [value] });
                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== displayItem.idGlobal), displayItem]);
                let _supplier = { displayName: displayItem.supplier, idGlobal: value.idSupplierGlobal } as IEntitySimpleDTO;
                setSupplier([...supplier.filter((x) => x.idGlobal !== displayItem.idGlobal), _supplier]);

                setItemsFilter([...itemsFilter, { idItem: value.idGlobal, quantityMax: 9999999, quantityMin: 0 }]);
                setItemsValidation([...itemsValidation, { err: false, message: "", idGlobal: value.idGlobal }]);
            },
            {
                goods: entity,
                idPricingModel: data.idPricingModelGlobal,
                idStore: data.idStoreGlobal,
                documentDate: data.documentDate
            }
        );
    }
    //stylesGridSelInput
    const storePanelStyle = errors.store ? styles.gridSelInputCustom  : '';
    const pricingModelStyle = errors.pricingModel ? styles.gridSelInputCustom  : '';
    
    const baseLineStore = errors.store ? styles.baseline  : '';
    const baseLinePricing = errors.pricingModel ? styles.baseline  : '';

    const isValidDocument = (items:IImportRemainsItemEditDTO[], showMessage: () => void): boolean => {
        var flag = true;
        if (!items || items.length === 0)
        {
            flag = false;
            showMessage();
        }
        return flag;
    }

    const calcRow = (value: IChangeDataGrid) => {
        const generateData = (data: IImportRemainsItemCreateDTO[] | IDisplayItem[]) => data.map((item) => item.idGlobal === value.idGlobal ? { ...item, [value.propertyName]: value.value } : item);
        setData(prev => ({ ...prev, items: generateData(prev.items) }));
        setDisplayItems(prev => (generateData(prev)))
    }

    const onOkAction = () => {
        lockingDocuments.delete({
            idTable: IdTableVariant.ImportRemains,
            idDocument: data.idGlobal as string,
        });

        props.ok(data);
    };

    const onCancel = () => {
        if (isCalculating && abortController) {
            handleCancelRecalculation()
        }

        lockingDocuments.delete({
            idTable: IdTableVariant.ImportRemains,
            idDocument: data.idGlobal as string,
        });

        props.cancel();
    };

    return (
        <>
            <BaseDocumentCreator
                valid={true}
                ok={{
                    onClick: () => {
                        if (isCalculating) {
                            renderGlobalAlert({
                                variant: "info",
                                title: `Процесс расчёта цен для позиций документа не завершён`,
                                statusCode: 0,
                                detail: "",
                                instance: "Сервис расчета цен",
                            })
                            return;
                        }
                        if (
                            isValid() && 
                            isValidDocument(data.items, ()=> setShowMessageModalValidDocument({message: errorsT("validDocument"), show: true })) === true && 
                            controlKiz(data.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "ok" }), DocumentType.importRemains) === true
                        ) {
                            onOkAction();
                        }
                    },
                    disabled: onlyRead || !!itemsValidation.find((x) => x.err == true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                save={{
                    onClick: () => {
                        if (isCalculating) {
                            renderGlobalAlert({
                                variant: "info",
                                title: `Процесс расчёта цен для позиций документа не завершён`,
                                statusCode: 0,
                                detail: "",
                                instance: "Сервис расчета цен",
                            })
                            return;
                        }
                        if (
                            isValid() && 
                            isValidDocument(data.items, ()=> setShowMessageModalValidDocument({message: errorsT("validDocument"), show: true })) === true &&
                            controlKiz(data.items, () => setShowMessageModal({ message: errorsT("kizCountNotMatch"), show: true, btSaveOrOk: "save" }), DocumentType.importRemains) === true
                        ) {
                            props.save(data);
                        }
                    },
                    disabled: onlyRead || !!itemsValidation.find((x) => x.err == true) || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                cancel={{ onClick: onCancel }}
                print={{
                    onClick: () => {
                        return "import_remains_plugin";
                    },
                    onSubItemClick: () => {
                        let arr = new Array<IKeyValuePair>();
                        if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined) arr.push({ key: props.idGlobal, value: props.document.mnemocode });
                        return arr;
                    },
                }}
            >
                <div className={styles.glwrapper}>
                    <div className={styles.wrapper}>
                        <div>
                            {isCompact ? (
                                <>
                                    <GridWrapper cols={35}>
                                        <FlexRow className={styles.compactFieldFields} wrap>
                                            <div>
                                                {isCalculating ?
                                                    (
                                                        <Button
                                                            disabled={isCancelling || !isCalculating}
                                                            className={styles.compactFieldCompactRecalculate}
                                                            variant="primary"
                                                            isLoadingSpinner={isCalculating}
                                                            onClick={() => {
                                                                handleCancelRecalculation()
                                                            }}
                                                        >
                                                            Остановить
                                                        </Button>
                                                    )
                                                    :
                                                    (<Button
                                                        disabled={(data.items && data?.items?.length > 0 && onlyRead) || isCalculating}
                                                        className={styles.compactFieldCompactRecalculate}
                                                        variant="primary"
                                                        onClick={async () => {
                                                            await handleRecalculate()
                                                        }}
                                                    >
                                                        Пересчитать
                                                    </Button>
                                                    )
                                                }
                                            </div>
                                            <TextInput
                                                label={t("documents.invoice.number")}
                                                inputId={"inputInvoiceNumber"}
                                                value={mnemocode}
                                                disabled
                                            />
                                            <DateInput
                                                required
                                                disabled={onlyRead ? true : false}
                                                labelVariant="required"
                                                inputId={"inputInvoiceDate"}
                                                label={t("documents.invoice.date")}
                                                value={data.documentDate}
                                                error={errors.documentDate}
                                                onBlur={(value) => {
                                                    setData({ ...data, documentDate: value as DateTime });
                                                }}
                                            />
                                            <StoreSelectorPanel
                                                we
                                                required
                                                compactMode
                                                idTable={idTable}
                                                contractor={{
                                                    label: t("documents.invoice.pharmacy"),
                                                    disabled: onlyRead ? true : false,
                                                    treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    label: t("documents.invoice.store"),
                                                    value: store, 
                                                    onSelect: (value) => {
                                                        setStore(value);
                                                    },
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead ? true : false
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore === true || props.variant === "edit" || props.variant === "copy"}
                                                documentStore={store ?? undefined}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                            <GridSelectorInput
                                                required
                                                disabled={onlyRead ? true : false}
                                                variant="required"
                                                selectorModalJsx={PricingModelSelectorModal}
                                                selectedEntity={pricingModel}
                                                label={t("documents.invoice.pricingModel")}
                                                id={"dictionaryInputPricingModel"}
                                                onSelect={(entity) => {
                                                    setPricingModel({ ...entity })
                                                }}
                                                error={errors.pricingModel}
                                                onClear={() => setPricingModel(null) }
                                                onFocus={() => {
                                                    setErrors({ ...errors, pricingModel: undefined });
                                                }}
                                            />
                                        </FlexRow>
                                        <div className={styles.compactFieldCompactMode}>
                                            <Tooltip title="Обычный режим">
                                                <StockBalanceButtonExpand
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                </>
                            ) : (
                                <>
                                    <GridWrapper cols={2} baseline>
                                        <TextInput
                                            className={styles.field_Number}
                                            disabled={true}
                                            label={t("documents.invoice.number")}
                                            inputId={"inputInvoiceNumber"}
                                            inline={true}
                                            value={mnemocode}
                                        />
                                        <DateInput
                                            required
                                            disabled={onlyRead ? true : false}
                                            labelVariant="required"
                                            inputId={"inputInvoiceDate"}
                                            className={styles.field_Date}
                                            label={t("documents.invoice.date")}
                                            inline={true}
                                            value={data.documentDate}
                                            error={errors.documentDate}
                                            onBlur={(value) => {
                                                setData({ ...data, documentDate: value as DateTime });
                                            }}
                                        />
                                        <div className={styles.field_CompactMode}>
                                            <Tooltip title="Компактный режим">
                                                <StockBalanceButtonCompress
                                                    onClick={() => {
                                                        setIsCompact(!isCompact);
                                                        setIsCompactStore(true);
                                                    }}
                                                    sizeVariant="normal"
                                                    colorVariant="default"
                                                />
                                            </Tooltip>
                                        </div>
                                    </GridWrapper>
                                    <div>
                                        <GridWrapper cols={2} className={baseLineStore}>
                                            <StoreSelectorPanel
                                                we
                                                required
                                                idTable={idTable}
                                                contractor={{ 
                                                    label: t("documents.invoice.pharmacy"),
                                                    disabled: onlyRead ? true : false,
                                                    treeViewCheckDirectoryType:'pharmacy'
                                                }}
                                                store={{
                                                    label: t("documents.invoice.store"),
                                                    value: store,
                                                    onSelect: (value) => {
                                                        setStore(value);
                                                    },
                                                    onClear: () => setStore(null),
                                                    error: errors.store,
                                                    onFocus: () => setErrors({ ...errors, store: undefined }),
                                                    disabled: onlyRead ? true : false,
                                                    className: storePanelStyle
                                                }}
                                                pricingModel={{
                                                    label: t("documents.invoice.pricingModel"),
                                                    onSelect: (value) => setPricingModel(value),
                                                }}
                                                notAutoComplete={isCompactStore || props.variant === "edit" || props.variant === "copy"}
                                                documentStore= {store ?? undefined}
                                                documentPricingModel={pricingModel ?? undefined}
                                            />
                                        </GridWrapper>
                                    </div>
                                    <GridWrapper cols={6} className={baseLinePricing}> 
                                        <GridSelectorInput
                                            required
                                            disabled={onlyRead}
                                            variant="required"
                                            className={`${styles.field_PriceModel} ${pricingModelStyle}`}
                                            selectorModalJsx={PricingModelSelectorModal}
                                            selectedEntity={pricingModel}
                                            label={t("documents.invoice.pricingModel")}
                                            id={"dictionaryInputPricingModel"}
                                            inline={true}
                                            onSelect={(entity) => {
                                                setPricingModel({ ...entity })
                                            }}
                                            error={errors.pricingModel}
                                            onClear={() => setPricingModel(null)
                                                    }
                                            onFocus={() => {
                                                setErrors({ ...errors, pricingModel: undefined });
                                            }}
                                        />
                                            {isCalculating ?
                                                (
                                                    <Button
                                                        disabled={isCancelling || !isCalculating}
                                                        className={styles.field_Recalculate}
                                                        variant="primary"
                                                        isLoadingSpinner={isCalculating}
                                                        onClick={() => {
                                                            handleCancelRecalculation()
                                                        }}
                                                    >
                                                        Остановить
                                                    </Button>
                                                )
                                                :
                                                (<Button
                                                    disabled={(data.items && data?.items?.length > 0 && onlyRead) || isCalculating}
                                                    className={styles.field_Recalculate}
                                                    variant="primary"
                                                    onClick={async () => {
                                                        await handleRecalculate()
                                                    }}
                                                >
                                                    Пересчитать
                                                </Button>
                                                )
                                            }
                                    </GridWrapper>
                                </>
                            )}
                            <FlexRow className={styles.field_flexbox}>
                                <PositionEditPanel
                                    kiz={{
                                        onClick: () => setShowKizScan(true),
                                        disabled: (selectedItemCreateDto?.isKiz || selectedItemCreateDto?.isKizBox) && onlyRead,
                                    }}
                                    edit={{
                                        onClick: () => {
                                            tabsContext.openChild(
                                                "import_remains_item_creator_plugin",
                                                "edit",
                                                undefined,
                                                (value: IImportRemainsItemCreateDTO, displayItem: IDisplayItem) => {
                                                    setData(prev => ({ ...prev, items: prev.items.map((item) => item.idGlobal === value.idGlobal ? { ...item, ...value } : item) }));
                                                    setDisplayItems(prev => prev.map((item) => item.idGlobal === displayItem.idGlobal ? {...item, ...displayItem } : item));

                                                    let _supplier = { displayName: displayItem.supplier, idGlobal: value.idSupplierGlobal } as IEntitySimpleDTO;
                                                    setSupplier(prev => prev.map((item) => item.idGlobal === _supplier.idGlobal ? { ...item, ..._supplier } : item));
                                                },
                                                {
                                                    item: data.items.find((x) => x.idGlobal === selectedItem?.idGlobal),
                                                    supplier: {
                                                        displayName: supplier.find((c) => c.idGlobal === data.items.find((x) => x.idGlobal === selectedItem?.idGlobal)?.idSupplierGlobal)
                                                            ?.displayName as string,
                                                        idGlobal: data.items.find((x) => x.idGlobal === selectedItem?.idGlobal)?.idSupplierGlobal as string,
                                                    } as IEntitySimpleDTO,
                                                    goods: { displayName: selectedItemGoods?.goodsName.name as string, idGlobal: selectedItemGoods?.idGlobal as string } as IEntitySimpleDTO,
                                                    idPricingModel: data.idPricingModelGlobal,
                                                    idStore: data.idStoreGlobal,
                                                    displayItem: displayItems.find((x) => x.idGlobal === selectedItem?.idGlobal),
                                                    onlyRead: onlyRead,
                                                    documentDate: data.documentDate,
                                                    scalingRatio: { displayName: selectedScalingRation?.displayName, idGlobal: selectedScalingRation?.idGlobal} as IEntitySimpleDTO,
                                                    variant: 'edit'
                                                }
                                            );
                                        },
                                        disabled: selectedItem ? false : true
                                    }}
                                    delete={{
                                        onClick: () => {
                                            setData({ ...data, items: [...data.items.filter((x) => x.idGlobal !== selectedItem?.idGlobal)] });
                                            setDisplayItems([...displayItems.filter((x) => x.idGlobal !== selectedItem?.idGlobal)]);

                                            const itemsValid = itemsValidation.filter((item) => item.idGlobal !== selectedItem?.idGlobal);
                                            const itemsFilterValid = itemsFilter.filter((item) => item.idItem !== selectedItem?.idGlobal);
                                            setItemsValidation(itemsValid);
                                            setItemsFilter(itemsFilterValid);
                                            setSelectedItem(undefined);
                                        },
                                        disabled: selectedItem && onlyRead
                                    }}
                                />
                                <GridSelectorInput
                                    disabled={onlyRead ? true : false}
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                    selectorModalJsx={GoodsSelectorModal}
                                    searchOption={searchOption.value}
                                    className={styles.field_SelectionGoods}
                                    id={"inputAccompanyingSelectionGoods"}
                                    inline={true}
                                    label={t("documents.invoice.selectionOfGoods")}
                                    //selectedEntity={goods}
                                    valid={() => isValid()}
                                    onSelect={(entity) => {
                                        if (searchOption.value === SearchOption.barcode || searchOption.value === SearchOption.goodsCode) {
                                            if (entity.length > 1) setGoodsViewItem({ item: entity, show: true });
                                            else
                                                callOpenImportRemainsItemCreatorPlugin(entity);
                                        } else {
                                            callOpenImportRemainsItemCreatorPlugin(entity);
                                        }
                                    }}
                                />
                                <Select
                                    disabled={onlyRead}
                                    barcodeScan={tabsContext.currentTab && tabsContext.currentTab?.id === currentIdTab.current}
                                    onSelect={(option) => {
                                        setSearchOption(option);
                                    }}
                                    value={searchOption}
                                    options={[
                                        { displayName: t("documents.invoice.barcode"), value: "barcode" },
                                        { displayName: t("documents.invoice.name"), value: "name" },
                                        { displayName: t("documents.invoice.goodsCode"), value: "goodsCode" },
                                    ]}
                                    defaultOption={defaultSearchOption}
                                />
                                <TextInput
                                    className={styles.field_Empty}
                                    disabled
                                    label=""
                                    inputId={"inputAccompanyingEmpty"}
                                    inline={true} /* value={accompanyingInvoiceInfo?.empty} onChange={(value) => setAccompanyingInvoiceInfo({ ...accompanyingInvoiceInfo, empty: value })}*/
                                />

                                <div className={styles.SettingsButton}>
                                    <SettingsButtonModal 
                                        disabled={onlyRead}
                                    />
                                </div>
                            </FlexRow>
                            <ChangeableGrid
                                virtualizedData
                                documentStateProc={onlyRead}
                                itemsFilter={itemsFilter}
                                itemsValidation={itemsValidation}
                                setItemsValidation={(value) => {
                                    const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item));
                                    setItemsValidation(newItemsValidation);
                                }}
                                gridId={gridId}
                                data={displayItems}
                                documentType={DocumentType.importRemains}
                                hiddenPagination={undefined}
                                filter={gridFilter}
                                totalCount={0}
                                getStyleCell={(cell, cells) => getStyleCell(cell, cells, DocumentType.importRemains)}
                                plugin={ImportRemainsCreatorItemGridPluginSettings}
                                selectedItem={selectedItem}
                                onSelect={(row) => {
                                    setSelectedItem(row);
                                }}
                                onDoubleClick={(row) => props.onDoubleClick?.(row)}
                                onSort={(i) => {
                                    dispatchGridFilter({ type: "sort", payload: i.propertyName });
                                }}
                                onFilterDelete={(i) => {
                                    dispatchGridFilter({
                                        type: "deleteColumnFilter",
                                        payload: i.propertyName,
                                    });
                                }}
                                onPageNumberChange={(n) => {
                                    dispatchGridFilter({
                                        type: "changePageNumber",
                                        payload: { pageNumber: n },
                                    });
                                }}
                                onNumberPerPageChange={(n) => {
                                    dispatchGridFilter({
                                        type: "changeNumberPerPage",
                                        payload: { numberPerPage: n },
                                    });
                                }}
                                onColumnEnter={(value: IChangeDataGrid) => clearTimer(() => calcRow(value))}
                                onChangeData={(value: IChangeDataGrid) => setTimer(() => calcRow(value))}
                                searching
                            />
                        </div>
                        <Accordion opened={false} caption="" id={`accompanyingInvoiceInfo-${uuidv4()}`} bold documentSticky>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent
                                    className={styles.field_BillNumber}
                                    labelClassName="label__width-111"
                                    disabled
                                    label={t("documents.invoice.averageMarkup")}
                                    inputId={"inputAccompanyingAverageMarkup"}
                                    value={+averageMarkup}
                                />
                            </GridWrapper>
                            <GridWrapper cols={3}>
                                <MoneyFormatComponent
                                    disabled
                                    labelClassName="label__width-111"
                                    label={t("documents.invoice.supplierVatAmounts")}
                                    inputId={"inputAccompanyingNdsSum"}
                                    value={Number(sum.amountSupplierVatSum)}
                                />
                                <MoneyFormatComponent
                                    disabled
                                    labelClassName="label__width-111"
                                    label={t("documents.invoice.supplierAmountsWithoutVat")}
                                    inputId={"inputAccompanyingNdsProviderSum"}
                                    value={Number(sum.amountSupplierSum)}
                                />
                                <MoneyFormatComponent
                                    disabled
                                    label={t("documents.invoice.supplierAmounts")}
                                    inputId={"inputAccompanyingProviderSum"}
                                    value={Number(sum.amountSupplierSumIncVat)}
                                />
                            </GridWrapper>

                            <GridWrapper cols={3}>
                                <MoneyFormatComponent
                                    disabled
                                    labelClassName="label__width-111"
                                    label={t("documents.invoice.retailAmountWithoutVat")}
                                    inputId={"inputAccompanyingRetailSumBNds"}
                                    value={Number(sum.amountRetailSum)}
                                />
                                <MoneyFormatComponent
                                    disabled
                                    labelClassName="label__width-111"
                                    label={t("documents.invoice.retailVatAmount")}
                                    inputId={"inputAccompanyingNdsRetailSumNds"}
                                    value={Number(sum.amountRetailVatSum)}
                                />
                                <MoneyFormatComponent 
                                    disabled 
                                    label={t("documents.invoice.retailAmount")} 
                                    inputId={"inputAccompanyingRetailSum"} 
                                    value={Number(sum.amountRetailSumIncVat)} 
                                />
                            </GridWrapper>
                        </Accordion>
                    </div>
                </div>

                {showKizScan && selectedScalingRation && (
                    <KizScanModal
                        ok={(value: IImportRemainsItemCreateDTO, scalingRatio: IScalingRatioDTO) => {
                            setData({ ...data, items: [...data.items.filter((x) => x.idGlobal !== value.idGlobal), {...value, dateCreated: DateTime.now()}] });
                            setDisplayItems(
                                displayItems.map((x): IDisplayItem => {
                                    return {
                                        ...x,
                                        countKizScan: x.idGlobal === value.idGlobal ? countKizSum(value, DocumentType.importRemains) : x.countKizScan,
                                        goodsName: x.goodsName,
                                        idGlobal: x.idGlobal,
                                        isKiz: x.isKiz,
                                        producerPrice: x.producerPrice,
                                        productMargin: x.productMargin,
                                        quantity: x.quantity,
                                        retailPrice: x.retailPrice,
                                        scalingRatioName: x.scalingRatioName,
                                        isGnvls: x.isGnvls,
                                        supplier: x.supplier,
                                        denominator: x.denominator,
                                        numerator: x.numerator,
                                        dateCreated:  x.idGlobal === value.idGlobal ? DateTime.now() : x.dateCreated,
                                        isOsu: value?.isOsu,
                                        goodsBarcodes: value?.goodsBarcodes
                                    };
                                })
                            );
                            setShowKizScan(false);
                        }}
                        cancel={() => setShowKizScan(false)}
                        selectedItem={selectedItemCreateDto as IImportRemainsItemCreateDTO}
                        documentType={DocumentType.importRemains}
                        document={{ idTable: IdTableVariant.ImportRemains, idDocument: data.idGlobal as string }}
                        idLotFrom={null}
                        scalingRatio={selectedScalingRation}
                        numerator={selectedScalingRation.numerator}
                        onValidateDuplicate={(barcode) => validateDuplicateKizs(data.items, barcode)}
                    />
                )}

                {showMessageModal.show && (	
                    <MessageModalWindow	
                        message={showMessageModal.message}	
                        ok={{	
                            onClick: () => {	
                                setShowMessageModal({ show: false, message: "" });	
                                showMessageModal.btSaveOrOk === "ok" ? onOkAction() : props.save(data);	
                            },	
                        }}	
                        cancel={{	
                            onClick: () => {	
                                setShowMessageModal({ show: false, message: "" });	
                            },	
                        }}	
                        primary	
                    />	
                )}

                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" });
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}

                {goodsViewItem.item && goodsViewItem.show && (
                    <InvoiceGoodsSelectModal
                        ok={(value: IEntitySimpleDTO) => {
                            callOpenImportRemainsItemCreatorPlugin(value);
                        }}
                        cancel={() => setGoodsViewItem({ item: goodsViewItem.item, show: false })}
                        data={goodsViewItem.item}
                    />
                )}
            </BaseDocumentCreator>
        </>
    );
};

export default ImportRemainsUpdateForm;
